import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Lazy } from 'src/components/lazy';
import {
  ProductTabMain,
  ProductTabProperties,
  ProductTabReviews,
  ProductTabStatistics,
  ProductTabStatisticsLegacyNewUi,
  ProductTabStatisticsLegacy,
} from './sections';
import AdContainer from 'src/components/ads';
import { PageSection } from 'src/components/page';
import ErrorBoundary from 'src/components/error-boundary';
import { useProductContext } from '../product-context';
import { useNewPriceHistoryExperiment } from 'src/hooks/experiments';
import { delayedScrollIntoView } from './utils';
import type { IProductPageTabsProps } from './types';
import { TabAnchor } from './types';
import { SectionWrapper } from './styles';

export const ProductTabs: React.FC<IProductPageTabsProps> = props => {
  const { recommendations, currentCampaign } = props;
  const { product } = useProductContext();
  const location = useLocation();
  const firstRender = useRef(true);
  const newPriceHistoryExperiment = useNewPriceHistoryExperiment();

  useEffect(() => {
    if (
      location.hash !== '' &&
      location.hash !== TabAnchor.ALL &&
      firstRender.current
    ) {
      firstRender.current = false;
      const targetElement = document.getElementById(location.hash)!;
      delayedScrollIntoView(targetElement);
    }
  }, [location.hash]);

  return (
    <>
      <SectionWrapper id={TabAnchor.ALL}>
        <ProductTabMain
          recommendations={recommendations}
          currentCampaign={currentCampaign}
        />
      </SectionWrapper>

      <SectionWrapper id={TabAnchor.REVIEWS}>
        <ErrorBoundary onError={() => null}>
          <Lazy>
            <ProductTabReviews product={product} />
          </Lazy>
        </ErrorBoundary>
      </SectionWrapper>

      <SectionWrapper
        id={TabAnchor.STATISTICS}
        $isNewpriceHistory={!!newPriceHistoryExperiment}
      >
        <PageSection noBorder noPad="bottom">
          <AdContainer
            placement="panorama_target"
            format="panorama_3_wde"
            min={861}
            max={10000}
            scalable
            scaleBase=".pj-ui-panel--row"
            spacing={{
              top: '10px',
              bottom: '10px',
            }}
          />

          <AdContainer
            placement="dubbelpanorama3"
            format="panorama_3"
            min={0}
            max={860}
            spacing={{
              top: '10px',
              bottom: '10px',
            }}
          />

          <ErrorBoundary onError={() => null}>
            <Lazy>
              {newPriceHistoryExperiment === 'new-using-new-data-source' && (
                <ProductTabStatistics />
              )}
              {newPriceHistoryExperiment === 'new-using-old-data-source' && (
                <ProductTabStatisticsLegacyNewUi />
              )}
              {!newPriceHistoryExperiment && <ProductTabStatisticsLegacy />}
            </Lazy>
          </ErrorBoundary>
        </PageSection>
      </SectionWrapper>

      <SectionWrapper id={TabAnchor.PROPERTIES}>
        <PageSection noBorder>
          <ErrorBoundary onError={() => null}>
            <Lazy>
              <ProductTabProperties />
            </Lazy>
          </ErrorBoundary>
        </PageSection>
      </SectionWrapper>
    </>
  );
};
